import { createSlice } from '@reduxjs/toolkit';
import { BLOCK_TYPES } from '@utils/constants.js';

// Custom-tab block types that support MULTIPLE blocks ("sections") in one tab.
// Limited to types without their own category grouping (Menu/Shop use categories).
// All four share one editor (CustomTabGroups) + one preview (CustomTabGroupsPreview),
// so they behave identically — verify Contacts first; the rest use the same path.
export const MULTI_BLOCK_TAB_TYPES = [BLOCK_TYPES.contacts, BLOCK_TYPES.faq, BLOCK_TYPES.review, BLOCK_TYPES.features];
export const isMultiBlockTabType = (blockType) => MULTI_BLOCK_TAB_TYPES.includes(blockType);

/**
 * Live-preview state for a custom tab that holds MULTIPLE blocks ("groups") of
 * one type (contacts / faq / review / features). The editor mirrors each block's
 * items here (keyed by blockId) so the preview phone can render every group with
 * live sync, while the tab-level design (view/layout) is read from the per-type
 * block slice (contacts/review/faq/features), since all groups share it.
 */
const initialState = {
  tabId: null,
  blockType: null,
  // ordered list of groups in the active custom tab: { blockId, title, items }
  blocks: [],
};

const normalizeBlock = (b) => ({ blockId: b.blockId, title: b.title ?? '', items: b.items ?? [] });

export const customTabPreviewSlice = createSlice({
  name: 'customTabPreview',
  initialState,
  reducers: {
    // Replace the whole tab context + ordered block skeleton (items may be empty).
    customTabPreviewSet: (state, action) => {
      const { tabId, blockType, blocks } = action.payload ?? {};
      state.tabId = tabId ?? null;
      state.blockType = blockType ?? null;
      state.blocks = Array.isArray(blocks) ? blocks.map(normalizeBlock) : [];
    },
    // Set (or insert) a block's items by blockId — called by each group editor on change.
    customTabPreviewUpsertItems: (state, action) => {
      const { blockId, items } = action.payload ?? {};
      const entry = state.blocks.find((b) => b.blockId === blockId);
      if (entry) entry.items = items ?? [];
      else state.blocks.push({ blockId, title: '', items: items ?? [] });
    },
    customTabPreviewUpdateTitle: (state, action) => {
      const { blockId, title } = action.payload ?? {};
      const entry = state.blocks.find((b) => b.blockId === blockId);
      if (entry) entry.title = title ?? '';
    },
    customTabPreviewAddBlock: (state, action) => {
      const { blockId, title } = action.payload ?? {};
      if (blockId != null && !state.blocks.some((b) => b.blockId === blockId)) {
        state.blocks.push({ blockId, title: title ?? '', items: [] });
      }
    },
    customTabPreviewRemoveBlock: (state, action) => {
      state.blocks = state.blocks.filter((b) => b.blockId !== action.payload);
    },
    customTabPreviewClear: () => initialState,
  },
});

export const {
  customTabPreviewSet,
  customTabPreviewUpsertItems,
  customTabPreviewUpdateTitle,
  customTabPreviewAddBlock,
  customTabPreviewRemoveBlock,
  customTabPreviewClear,
} = customTabPreviewSlice.actions;

export const selectCustomTabPreviewTabId = (state) => state.customTabPreview.tabId;
export const selectCustomTabPreviewBlockType = (state) => state.customTabPreview.blockType;
export const selectCustomTabPreviewBlocks = (state) => state.customTabPreview.blocks;

export default customTabPreviewSlice.reducer;
